import type { DragEndEvent, DragStartEvent } from '@dnd-kit/core';
import {
    DndContext,
    DragOverlay,
    PointerSensor,
    useDraggable,
    useDroppable,
    useSensor,
    useSensors,
} from '@dnd-kit/core';
import { arrayMove, SortableContext, useSortable, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import React, { useEffect, useMemo, useState } from 'react';
import { LuArrowDown, LuArrowUp, LuGripVertical, LuTrash2 } from 'react-icons/lu';

import {
    GUID,
    InternalUserProfile,
    type InternalUserProfileRequest,
    Tenant,
    TenantTeamMember,
    TenantTeamMemberConfig,
    TenantTeamRole,
    TenantTeamView,
} from '@ndvr/auth-api';

import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle } from '@/components/ui/sheet';
import { useInternalUserProfilesQuery } from '@/modules/internal-user-queries';
import { cn } from '@/utils/cn';

import { useUpdateInternalUserProfileMutation } from '../internalUserProfiles/internal-user-profile-queries';
import { InternalUserProfileForm } from '../internalUserProfiles/InternalUserProfileForm';
import {
    useSaveTenantTeamMutation,
    useTenantListQuery,
    useTenantTeamMemberCandidatesQuery,
    useTenantTeamQuery,
} from './tenant-team-queries';

type EditableMember = TenantTeamMember;
const ASSIGNABLE_ROLES = [
    TenantTeamRole.ADVISOR,
    TenantTeamRole.CLIENT_SUPPORT,
    TenantTeamRole.LEGAL_AND_COMPLIANCE,
] as const;
type AssignableRole = (typeof ASSIGNABLE_ROLES)[number];
type MembersByRole = Record<AssignableRole, EditableMember[]>;
const ASSIGNABLE_ROLE_VALUES = new Set<string>(ASSIGNABLE_ROLES);

// Mirrors the client-portal advisor shape in client/store/advisor/advisors.ts.
type AdvisorCardBase = {
    name: string;
    title: string;
    email: string;
    phone: string;
    about?: string;
    image?: string;
    calendar?: string;
};

type ActiveTeamDrag =
    | {
          source: 'internal';
          advisor: AdvisorCardBase;
      }
    | {
          source: 'assigned';
          advisor: AdvisorCardBase;
      };

const INTERNAL_USER_PREFIX = 'internal-user:';
const ASSIGNED_MEMBER_PREFIX = 'assigned-member:';
const TEAM_ZONE_PREFIX = 'team-zone:';

const ROLE_CONFIG: Record<AssignableRole, { title: string; emptyMessage: string; addLabel: string }> = {
    [TenantTeamRole.ADVISOR]: {
        title: 'Advisors',
        emptyMessage: 'No advisor assigned.',
        addLabel: '+ Advisor',
    },
    [TenantTeamRole.CLIENT_SUPPORT]: {
        title: 'Client Support Team',
        emptyMessage: 'No client support team assigned.',
        addLabel: '+ Client Support Team',
    },
    [TenantTeamRole.LEGAL_AND_COMPLIANCE]: {
        title: 'Legal/Compliance',
        emptyMessage: 'No legal/compliance team assigned.',
        addLabel: '+ Legal/Compliance',
    },
};

export const TenantTeamAdmin = () => {
    const { data: tenants = [], isLoading: tenantsLoading } = useTenantListQuery();
    const { data: internalUserProfiles = [] } = useInternalUserProfilesQuery();
    const [selectedTenantGUID, setSelectedTenantGUID] = useState<GUID<Tenant> | undefined>(undefined);
    const [membersByRole, setMembersByRole] = useState<MembersByRole>(() => buildEmptyMembersByRole());
    const [saveError, setSaveError] = useState<string | null>(null);
    const [profileSaveError, setProfileSaveError] = useState<string | null>(null);
    const [editingProfileGUID, setEditingProfileGUID] = useState<string | null>(null);

    const { data: team, isLoading: teamLoading, isError: teamLoadError } = useTenantTeamQuery(selectedTenantGUID);
    const { data: candidates = [] } = useTenantTeamMemberCandidatesQuery(selectedTenantGUID);
    const saveTenantTeamMutation = useSaveTenantTeamMutation();
    const updateInternalUserProfileMutation = useUpdateInternalUserProfileMutation();

    useEffect(() => {
        if (!selectedTenantGUID && tenants.length > 0) {
            setSelectedTenantGUID(tenants[0].tenantGUID);
        }
    }, [selectedTenantGUID, tenants]);

    useEffect(() => {
        if (!team) {
            return;
        }
        setMembersByRole(buildMembersByRoleFromTeam(team));
        setSaveError(null);
    }, [team]);

    useEffect(() => {
        setEditingProfileGUID(null);
        setProfileSaveError(null);
    }, [selectedTenantGUID]);

    const selectedMemberGUIDs = useMemo(
        () => new Set(ASSIGNABLE_ROLES.flatMap((role) => membersByRole[role]).map((member) => member.internalUserGUID)),
        [membersByRole],
    );

    const tenantInternalUsers = useMemo(() => {
        const userByGuid = new Map<string, InternalUserProfile>();
        [...candidates].forEach((candidate) => {
            userByGuid.set(candidate.profileGUID, candidate);
        });

        return [...userByGuid.values()].sort((left, right) =>
            buildName(left).localeCompare(buildName(right), undefined, { sensitivity: 'base' }),
        );
    }, [candidates]);

    const internalUserProfilesByGUID = useMemo(() => {
        const profileByGUID = new Map<string, InternalUserProfile>();
        internalUserProfiles
            .filter((profile) => !profile.archived)
            .forEach((profile) => profileByGUID.set(profile.profileGUID, profile));
        tenantInternalUsers.forEach((profile) => {
            if (!profileByGUID.has(profile.profileGUID)) {
                profileByGUID.set(profile.profileGUID, profile);
            }
        });
        return profileByGUID;
    }, [internalUserProfiles, tenantInternalUsers]);

    const editingProfile = useMemo(
        () => (editingProfileGUID ? internalUserProfilesByGUID.get(editingProfileGUID) : undefined),
        [editingProfileGUID, internalUserProfilesByGUID],
    );

    const closeProfileDrawer = () => {
        setEditingProfileGUID(null);
        setProfileSaveError(null);
    };

    const openProfileDrawer = (internalUserGUID: string) => {
        setEditingProfileGUID(internalUserGUID);
        setProfileSaveError(null);
    };

    const handleSaveInternalUserProfile = async (profileUpdate: InternalUserProfileRequest) => {
        if (!editingProfile) {
            return;
        }

        try {
            await updateInternalUserProfileMutation.mutateAsync({
                profileGUID: editingProfile.profileGUID,
                profile: profileUpdate,
            });

            const updatedProfile: InternalUserProfile = {
                ...editingProfile,
                email: profileUpdate.email,
                firstName: profileUpdate.firstName ?? '',
                lastName: profileUpdate.lastName ?? '',
                title: profileUpdate.title ?? '',
                phone: profileUpdate.phone ?? '',
                bio: profileUpdate.bio ?? '',
                calendarLink: profileUpdate.calendarLink ?? '',
                imageSrc: profileUpdate.imageSrc ?? '',
            };

            setMembersByRole((current) =>
                mapMembersByRole(current, (members) => syncRoleMembersWithProfile(members, updatedProfile)),
            );
            setProfileSaveError(null);
            setEditingProfileGUID(null);
        } catch (error: unknown) {
            setProfileSaveError(error instanceof Error ? error.message : 'Failed to update internal user profile.');
        }
    };

    const moveMember = (members: EditableMember[], index: number, direction: 'up' | 'down') => {
        const nextIndex = direction === 'up' ? index - 1 : index + 1;
        if (nextIndex < 0 || nextIndex >= members.length) {
            return members;
        }
        const copy = [...members];
        [copy[index], copy[nextIndex]] = [copy[nextIndex], copy[index]];
        return normalizeMemberDisplayOrder(copy);
    };

    const reorderMembers = (members: EditableMember[], activeIndex: number, overIndex: number) => {
        if (activeIndex < 0 || overIndex < 0 || activeIndex === overIndex) {
            return members;
        }
        return normalizeMemberDisplayOrder(arrayMove(members, activeIndex, overIndex));
    };

    const setRoleMembers = (role: AssignableRole, updateMembers: (members: EditableMember[]) => EditableMember[]) => {
        setMembersByRole((current) => ({
            ...current,
            [role]: updateMembers(current[role]),
        }));
    };

    const addMemberToRole = (profile: InternalUserProfile, role: AssignableRole, insertAt?: number) => {
        setRoleMembers(role, (current) => {
            if (current.some((member) => member.internalUserGUID === profile.profileGUID)) {
                return current;
            }
            const next = [...current];
            const member = buildMemberFromProfile(profile, role);
            if (insertAt === undefined || insertAt < 0 || insertAt > next.length) {
                next.push(member);
            } else {
                next.splice(insertAt, 0, member);
            }
            return normalizeMemberDisplayOrder(next);
        });
    };

    const removeMemberFromRole = (role: AssignableRole, internalUserGUID: string) => {
        setRoleMembers(role, (current) =>
            normalizeMemberDisplayOrder(current.filter((member) => member.internalUserGUID !== internalUserGUID)),
        );
    };

    const moveRoleMember = (role: AssignableRole, index: number, direction: 'up' | 'down') => {
        setRoleMembers(role, (current) => moveMember(current, index, direction));
    };

    const reorderRoleMembers = (role: AssignableRole, activeIndex: number, overIndex: number) => {
        setRoleMembers(role, (current) => reorderMembers(current, activeIndex, overIndex));
    };

    const saveTeam = async () => {
        if (!selectedTenantGUID) {
            return;
        }
        const members: TenantTeamMemberConfig[] = ASSIGNABLE_ROLES.flatMap((role) =>
            membersByRole[role].map((member, index) => ({
                internalUserGUID: member.internalUserGUID,
                role,
                displayOrder: index,
            })),
        );

        try {
            const response = await saveTenantTeamMutation.mutateAsync({
                tenantGUID: selectedTenantGUID,
                members,
            });
            setMembersByRole(buildMembersByRoleFromTeam(response));
            setSaveError(null);
        } catch (error: unknown) {
            setSaveError(error instanceof Error ? error.message : 'Failed to save tenant team.');
        }
    };

    return (
        <div className="space-y-4 px-8 pb-8">
            <Card>
                <CardHeader>
                    <CardTitle className="font-heading font-medium">Tenant Team</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                    <div className="max-w-xl space-y-2">
                        <label className="text-sm font-medium">Tenant</label>
                        <Select
                            value={selectedTenantGUID ?? ''}
                            onValueChange={(tenantGUID) => setSelectedTenantGUID(tenantGUID)}
                            disabled={tenantsLoading}
                        >
                            <SelectTrigger>
                                <SelectValue placeholder={tenantsLoading ? 'Loading tenants...' : 'Select tenant'} />
                            </SelectTrigger>
                            <SelectContent>
                                {tenants.map((tenant) => (
                                    <SelectItem key={tenant.tenantGUID} value={tenant.tenantGUID}>
                                        {tenant.name}
                                    </SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                    </div>

                    {teamLoading && <div className="text-muted-foreground text-sm">Loading tenant team...</div>}
                    {teamLoadError && (
                        <div className="text-destructive text-sm">Failed to load tenant team for this tenant.</div>
                    )}

                    {!teamLoading && !teamLoadError && selectedTenantGUID && (
                        <TeamAssignmentSection
                            internalUsers={tenantInternalUsers}
                            internalUserProfilesByGUID={internalUserProfilesByGUID}
                            membersByRole={membersByRole}
                            selectedMemberGUIDs={selectedMemberGUIDs}
                            onAddToRole={addMemberToRole}
                            onRemoveFromRole={removeMemberFromRole}
                            onMoveRoleMember={moveRoleMember}
                            onReorderRoleMembers={reorderRoleMembers}
                            onEditMember={openProfileDrawer}
                        />
                    )}

                    {saveError && (
                        <div className="text-destructive rounded-md border border-red-300 bg-red-50 p-3 text-sm">
                            {saveError}
                        </div>
                    )}

                    <div className="flex justify-end">
                        <Button onClick={saveTeam} disabled={!selectedTenantGUID || saveTenantTeamMutation.isPending}>
                            {saveTenantTeamMutation.isPending ? 'Saving...' : 'Save team'}
                        </Button>
                    </div>
                </CardContent>
            </Card>

            <Sheet open={!!editingProfileGUID} onOpenChange={(open) => !open && closeProfileDrawer()}>
                <SheetContent className="w-full overflow-y-auto sm:max-w-2xl">
                    <SheetHeader>
                        <SheetTitle>Edit Internal User</SheetTitle>
                        <SheetDescription>
                            Update this team member profile directly from tenant team management.
                        </SheetDescription>
                    </SheetHeader>

                    <div className="mt-6 space-y-4">
                        {profileSaveError && (
                            <div className="text-destructive rounded-md border border-red-300 bg-red-50 p-3 text-sm">
                                {profileSaveError}
                            </div>
                        )}

                        {editingProfile ? (
                            <InternalUserProfileForm
                                profile={editingProfile}
                                isPending={updateInternalUserProfileMutation.isPending}
                                onSubmit={handleSaveInternalUserProfile}
                                onCancel={closeProfileDrawer}
                            />
                        ) : (
                            <div className="text-muted-foreground text-sm">Internal user profile not found.</div>
                        )}
                    </div>
                </SheetContent>
            </Sheet>
        </div>
    );
};

const TeamAssignmentSection = ({
    internalUsers,
    internalUserProfilesByGUID,
    membersByRole,
    selectedMemberGUIDs,
    onAddToRole,
    onRemoveFromRole,
    onMoveRoleMember,
    onReorderRoleMembers,
    onEditMember,
}: {
    internalUsers: InternalUserProfile[];
    internalUserProfilesByGUID: Map<string, InternalUserProfile>;
    membersByRole: MembersByRole;
    selectedMemberGUIDs: Set<string>;
    onAddToRole: (profile: InternalUserProfile, role: AssignableRole, insertAt?: number) => void;
    onRemoveFromRole: (role: AssignableRole, internalUserGUID: string) => void;
    onMoveRoleMember: (role: AssignableRole, index: number, direction: 'up' | 'down') => void;
    onReorderRoleMembers: (role: AssignableRole, activeIndex: number, overIndex: number) => void;
    onEditMember: (internalUserGUID: string) => void;
}) => {
    const [activeDrag, setActiveDrag] = useState<ActiveTeamDrag | null>(null);
    const sensors = useSensors(useSensor(PointerSensor));

    const handleDragStart = ({ active }: DragStartEvent) => {
        const activeId = String(active.id);
        const internalUserGUID = parseInternalUserGUID(activeId);
        if (internalUserGUID) {
            const internalUser = internalUsers.find((profile) => profile.profileGUID === internalUserGUID);
            if (internalUser) {
                setActiveDrag({
                    source: 'internal',
                    advisor: advisorCardFromProfile(internalUser),
                });
            }
            return;
        }

        const assignedMember = parseAssignedMemberId(activeId);
        if (!assignedMember) {
            setActiveDrag(null);
            return;
        }

        const member = membersByRole[assignedMember.role].find(
            (assigned) => assigned.internalUserGUID === assignedMember.internalUserGUID,
        );
        if (member) {
            setActiveDrag({
                source: 'assigned',
                advisor: advisorCardFromMember(member, internalUserProfilesByGUID.get(member.internalUserGUID)),
            });
        }
    };

    const handleDragEnd = ({ active, over }: DragEndEvent) => {
        setActiveDrag(null);
        if (!over) {
            return;
        }

        const activeId = String(active.id);
        const overId = String(over.id);
        const dropRole = roleFromDropTargetId(overId);

        const internalUserGUID = parseInternalUserGUID(activeId);
        if (internalUserGUID) {
            if (!dropRole) {
                return;
            }

            const internalUser = internalUsers.find((profile) => profile.profileGUID === internalUserGUID);
            if (!internalUser) {
                return;
            }

            if (selectedMemberGUIDs.has(internalUserGUID)) {
                return;
            }

            const dropOnMember = parseAssignedMemberId(overId);
            const insertAt =
                dropOnMember && dropOnMember.role === dropRole
                    ? membersByRole[dropRole].findIndex(
                          (member) => member.internalUserGUID === dropOnMember.internalUserGUID,
                      )
                    : undefined;

            onAddToRole(internalUser, dropRole, insertAt);
            return;
        }

        const assignedMember = parseAssignedMemberId(activeId);
        if (!assignedMember || !dropRole || assignedMember.role !== dropRole) {
            return;
        }

        const members = membersByRole[assignedMember.role];
        const activeIndex = members.findIndex((member) => member.internalUserGUID === assignedMember.internalUserGUID);
        if (activeIndex === -1) {
            return;
        }

        const dropOnMember = parseAssignedMemberId(overId);
        if (!dropOnMember) {
            const endIndex = members.length - 1;
            if (endIndex > -1) {
                onReorderRoleMembers(assignedMember.role, activeIndex, endIndex);
            }
            return;
        }

        if (dropOnMember.role !== assignedMember.role) {
            return;
        }

        const overIndex = members.findIndex((member) => member.internalUserGUID === dropOnMember.internalUserGUID);
        if (overIndex === -1 || activeIndex === overIndex) {
            return;
        }

        onReorderRoleMembers(assignedMember.role, activeIndex, overIndex);
    };

    return (
        <DndContext sensors={sensors} onDragStart={handleDragStart} onDragEnd={handleDragEnd}>
            <div className="grid gap-4 xl:grid-cols-2">
                <InternalUsersPanel
                    internalUsers={internalUsers}
                    selectedMemberGUIDs={selectedMemberGUIDs}
                    onAddToRole={onAddToRole}
                />

                <FinalTeamPanel
                    membersByRole={membersByRole}
                    internalUserProfilesByGUID={internalUserProfilesByGUID}
                    onMoveRoleMember={onMoveRoleMember}
                    onRemoveFromRole={onRemoveFromRole}
                    onEditMember={onEditMember}
                />
            </div>

            <DragOverlay>
                {activeDrag && (
                    <div className="w-full max-w-md opacity-90">
                        <AdvisorContent advisor={activeDrag.advisor} />
                    </div>
                )}
            </DragOverlay>
        </DndContext>
    );
};

const InternalUsersPanel = ({
    internalUsers,
    selectedMemberGUIDs,
    onAddToRole,
}: {
    internalUsers: InternalUserProfile[];
    selectedMemberGUIDs: Set<string>;
    onAddToRole: (profile: InternalUserProfile, role: AssignableRole) => void;
}) => {
    const availableInternalUsers = internalUsers.filter((user) => !selectedMemberGUIDs.has(user.profileGUID));

    return (
        <div className="space-y-3 rounded-md border p-3">
            <div>
                <div className="font-heading text-sm font-semibold">Internal users linked to tenant permissions</div>
                <div className="text-muted-foreground text-xs">
                    Hover a user to assign with buttons, or drag into a role on the right.
                </div>
            </div>

            {availableInternalUsers.length === 0 && (
                <div className="text-muted-foreground rounded-md border border-dashed p-3 text-sm">
                    No eligible internal users available.
                </div>
            )}

            {availableInternalUsers.map((user) => (
                <InternalUserRow
                    key={user.profileGUID}
                    user={user}
                    canAdd
                    onAddToRole={(role) => onAddToRole(user, role)}
                />
            ))}
        </div>
    );
};

const InternalUserRow = ({
    user,
    canAdd,
    onAddToRole,
}: {
    user: InternalUserProfile;
    canAdd: boolean;
    onAddToRole: (role: AssignableRole) => void;
}) => {
    const draggable = useDraggable({
        id: buildInternalUserId(user.profileGUID),
        disabled: !canAdd,
    });
    const { attributes, listeners, setNodeRef, transform, isDragging } = draggable;
    const displayName = buildName(user);

    return (
        <div
            ref={setNodeRef}
            style={{ transform: CSS.Translate.toString(transform) }}
            className={cn('group rounded-md border p-3 transition-colors', isDragging && 'opacity-60')}
        >
            <div className="flex items-center gap-3">
                <Avatar className="h-10 w-10">
                    <AvatarFallback>{getInitials(displayName)}</AvatarFallback>
                </Avatar>

                <div className="min-w-0 flex-1">
                    <div className="truncate text-sm font-medium">{displayName}</div>
                    <div className="text-muted-foreground truncate text-xs">{user.email}</div>
                </div>

                <div className="flex items-center gap-1 md:opacity-0 md:transition-opacity md:group-focus-within:opacity-100 md:group-hover:opacity-100">
                    {ASSIGNABLE_ROLES.map((role) => (
                        <Button
                            key={role}
                            type="button"
                            variant="outline"
                            size="sm"
                            className="h-8 px-2 text-xs"
                            disabled={!canAdd}
                            onClick={() => onAddToRole(role)}
                        >
                            {ROLE_CONFIG[role].addLabel}
                        </Button>
                    ))}
                    <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        className="cursor-grab"
                        aria-label={`Drag ${displayName}`}
                        {...listeners}
                        {...attributes}
                    >
                        <LuGripVertical className="h-4 w-4" />
                    </Button>
                </div>
            </div>
        </div>
    );
};

const FinalTeamPanel = ({
    membersByRole,
    internalUserProfilesByGUID,
    onMoveRoleMember,
    onRemoveFromRole,
    onEditMember,
}: {
    membersByRole: MembersByRole;
    internalUserProfilesByGUID: Map<string, InternalUserProfile>;
    onMoveRoleMember: (role: AssignableRole, index: number, direction: 'up' | 'down') => void;
    onRemoveFromRole: (role: AssignableRole, internalUserGUID: string) => void;
    onEditMember: (internalUserGUID: string) => void;
}) => (
    <div className="space-y-3 rounded-md border p-3">
        <div>
            <div className="font-heading text-sm font-semibold">Final team structure</div>
            <div className="text-muted-foreground text-xs">
                Advisors appear first, then client support and legal/compliance.
            </div>
        </div>

        <div className="overflow-hidden rounded-md border">
            {ASSIGNABLE_ROLES.map((role, index) => (
                <React.Fragment key={role}>
                    {index > 0 && <div className="border-t" />}
                    <RoleSection
                        role={role}
                        title={ROLE_CONFIG[role].title}
                        members={membersByRole[role]}
                        internalUserProfilesByGUID={internalUserProfilesByGUID}
                        emptyMessage={ROLE_CONFIG[role].emptyMessage}
                        onMoveUp={(memberIndex) => onMoveRoleMember(role, memberIndex, 'up')}
                        onMoveDown={(memberIndex) => onMoveRoleMember(role, memberIndex, 'down')}
                        onRemove={(internalUserGUID) => onRemoveFromRole(role, internalUserGUID)}
                        onEditMember={onEditMember}
                    />
                </React.Fragment>
            ))}
        </div>
    </div>
);

const RoleSection = ({
    role,
    title,
    members,
    internalUserProfilesByGUID,
    emptyMessage,
    onMoveUp,
    onMoveDown,
    onRemove,
    onEditMember,
}: {
    role: AssignableRole;
    title: string;
    members: EditableMember[];
    internalUserProfilesByGUID: Map<string, InternalUserProfile>;
    emptyMessage: string;
    onMoveUp: (index: number) => void;
    onMoveDown: (index: number) => void;
    onRemove: (internalUserGUID: string) => void;
    onEditMember: (internalUserGUID: string) => void;
}) => {
    const zoneId = zoneIdForRole(role);
    const { setNodeRef, isOver } = useDroppable({
        id: zoneId,
    });

    return (
        <div ref={setNodeRef} className={cn('transition-colors', isOver && 'bg-primary/5')}>
            <div className="bg-muted/40 text-muted-foreground border-b px-3 py-2 text-xs font-semibold tracking-wide uppercase">
                {title}
            </div>

            {members.length === 0 && <div className="text-muted-foreground px-3 py-3 text-sm">{emptyMessage}</div>}

            {members.length > 0 && (
                <SortableContext
                    items={members.map((member) => buildAssignedMemberId(role, member.internalUserGUID))}
                    strategy={verticalListSortingStrategy}
                >
                    <div>
                        {members.map((member, index) => (
                            <AssignedTeamMemberRow
                                key={member.internalUserGUID}
                                role={role}
                                member={member}
                                profile={internalUserProfilesByGUID.get(member.internalUserGUID)}
                                isLast={index === members.length - 1}
                                onMoveUp={() => onMoveUp(index)}
                                onMoveDown={() => onMoveDown(index)}
                                onRemove={() => onRemove(member.internalUserGUID)}
                                onEditProfile={() => onEditMember(member.internalUserGUID)}
                                disableMoveUp={index === 0}
                                disableMoveDown={index === members.length - 1}
                            />
                        ))}
                    </div>
                </SortableContext>
            )}
        </div>
    );
};

const AssignedTeamMemberRow = ({
    role,
    member,
    profile,
    isLast,
    onMoveUp,
    onMoveDown,
    onRemove,
    onEditProfile,
    disableMoveUp,
    disableMoveDown,
}: {
    role: AssignableRole;
    member: EditableMember;
    profile?: InternalUserProfile;
    isLast: boolean;
    onMoveUp: () => void;
    onMoveDown: () => void;
    onRemove: () => void;
    onEditProfile: () => void;
    disableMoveUp: boolean;
    disableMoveDown: boolean;
}) => {
    const sortable = useSortable({
        id: buildAssignedMemberId(role, member.internalUserGUID),
    });
    const advisor = advisorCardFromMember(member, profile);
    const { setNodeRef, transform, transition, isDragging, attributes, listeners } = sortable;

    return (
        <div
            ref={setNodeRef}
            style={{ transform: CSS.Transform.toString(transform), transition }}
            className={cn('bg-background', !isLast && 'border-b', isDragging && 'opacity-60')}
        >
            <div className="flex items-start gap-3 px-3 py-3">
                <Avatar className="h-10 w-10">
                    <AvatarFallback>{getInitials(advisor.name)}</AvatarFallback>
                </Avatar>

                <div className="min-w-0 flex-1">
                    <AdvisorContent advisor={advisor} onNameClick={onEditProfile} />
                </div>

                <div className="flex items-center gap-1">
                    <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        onClick={onMoveUp}
                        disabled={disableMoveUp}
                        aria-label={`Move ${member.name || member.email} up`}
                    >
                        <LuArrowUp className="h-4 w-4" />
                    </Button>
                    <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        onClick={onMoveDown}
                        disabled={disableMoveDown}
                        aria-label={`Move ${member.name || member.email} down`}
                    >
                        <LuArrowDown className="h-4 w-4" />
                    </Button>
                    <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        className="cursor-grab"
                        aria-label={`Drag ${member.name || member.email}`}
                        {...listeners}
                        {...attributes}
                    >
                        <LuGripVertical className="h-4 w-4" />
                    </Button>
                    <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        onClick={onRemove}
                        aria-label={`Remove ${member.name || member.email}`}
                    >
                        <LuTrash2 className="h-4 w-4" />
                    </Button>
                </div>
            </div>
        </div>
    );
};

const AdvisorContent = ({ advisor, onNameClick }: { advisor: AdvisorCardBase; onNameClick?: () => void }) => (
    <div className="min-w-0">
        {onNameClick ? (
            <button
                type="button"
                className="hover:text-primary truncate text-left text-sm font-medium transition-colors hover:underline"
                onClick={onNameClick}
            >
                {advisor.name || 'NDVR Team Member'}
            </button>
        ) : (
            <div className="truncate text-sm font-medium">{advisor.name || 'NDVR Team Member'}</div>
        )}
        <div className="text-muted-foreground truncate text-xs">{advisor.title || 'No title'}</div>
        {advisor.email && <div className="text-primary truncate text-xs">{advisor.email}</div>}
        {advisor.phone && <div className="text-primary truncate text-xs">{advisor.phone}</div>}
    </div>
);

const buildName = (profile: InternalUserProfile) => {
    const firstName = profile.firstName?.trim();
    const lastName = profile.lastName?.trim();
    const fullName = [firstName, lastName].filter(Boolean).join(' ').trim();
    return fullName || profile.email;
};

const buildMemberFromProfile = (profile: InternalUserProfile, role: AssignableRole): EditableMember => ({
    internalUserGUID: profile.profileGUID,
    role,
    displayOrder: 0,
    name: buildName(profile),
    title: profile.title ?? '',
    email: profile.email,
    phone: profile.phone ?? '',
    bio: profile.bio ?? '',
    calendarLink: profile.calendarLink ?? '',
    imageSrc: profile.imageSrc ?? '',
});

const syncRoleMembersWithProfile = (members: EditableMember[], profile: InternalUserProfile): EditableMember[] =>
    members.map((member) => {
        if (member.internalUserGUID !== profile.profileGUID) {
            return member;
        }

        return {
            ...member,
            name: buildName(profile),
            title: profile.title ?? '',
            email: profile.email,
            phone: profile.phone ?? '',
            bio: profile.bio ?? '',
            calendarLink: profile.calendarLink ?? '',
            imageSrc: profile.imageSrc ?? '',
        };
    });

const normalizeMemberDisplayOrder = (members: EditableMember[]) =>
    members.map((member, index) => ({
        ...member,
        displayOrder: index,
    }));

const advisorCardFromProfile = (profile: InternalUserProfile): AdvisorCardBase => ({
    name: buildName(profile),
    title: 'Team member',
    email: profile.email,
    phone: '',
});

const advisorCardFromMember = (member: EditableMember, profile?: InternalUserProfile): AdvisorCardBase => ({
    name: (profile ? buildName(profile) : '') || member.name || member.email || profile?.email || '',
    title: profile?.title || member.title || 'No title',
    email: profile?.email || member.email || '',
    phone: profile?.phone || member.phone || '',
});

const zoneIdForRole = (role: AssignableRole) => `${TEAM_ZONE_PREFIX}${role}`;

const roleFromZoneId = (zoneId: string): AssignableRole | null =>
    zoneId.startsWith(TEAM_ZONE_PREFIX) ? parseAssignableRole(zoneId.slice(TEAM_ZONE_PREFIX.length)) : null;

const buildInternalUserId = (profileGUID: string) => `${INTERNAL_USER_PREFIX}${profileGUID}`;
const parseInternalUserGUID = (id: string) =>
    id.startsWith(INTERNAL_USER_PREFIX) ? id.slice(INTERNAL_USER_PREFIX.length) : null;

const buildAssignedMemberId = (role: AssignableRole, internalUserGUID: string) =>
    `${ASSIGNED_MEMBER_PREFIX}${role}:${internalUserGUID}`;

const parseAssignableRole = (value: string): AssignableRole | null => (isAssignableRole(value) ? value : null);

const isAssignableRole = (value: string): value is AssignableRole => ASSIGNABLE_ROLE_VALUES.has(value);

const parseAssignedMemberId = (id: string): { role: AssignableRole; internalUserGUID: string } | null => {
    if (!id.startsWith(ASSIGNED_MEMBER_PREFIX)) {
        return null;
    }

    const payload = id.slice(ASSIGNED_MEMBER_PREFIX.length);
    const separatorIndex = payload.indexOf(':');
    if (separatorIndex === -1) {
        return null;
    }

    const role = parseAssignableRole(payload.slice(0, separatorIndex));
    const internalUserGUID = payload.slice(separatorIndex + 1);
    if (!role || !internalUserGUID) {
        return null;
    }

    return {
        role,
        internalUserGUID,
    };
};

const roleFromDropTargetId = (id: string): AssignableRole | null => {
    const roleFromZone = roleFromZoneId(id);
    if (roleFromZone) {
        return roleFromZone;
    }
    const assignedMember = parseAssignedMemberId(id);
    return assignedMember?.role ?? null;
};

const buildEmptyMembersByRole = (): MembersByRole => ({
    [TenantTeamRole.ADVISOR]: [],
    [TenantTeamRole.CLIENT_SUPPORT]: [],
    [TenantTeamRole.LEGAL_AND_COMPLIANCE]: [],
});

const buildMembersByRoleFromTeam = (team: TenantTeamView): MembersByRole => ({
    [TenantTeamRole.ADVISOR]: team.advisors ?? [],
    [TenantTeamRole.CLIENT_SUPPORT]: team.clientSupport ?? [],
    [TenantTeamRole.LEGAL_AND_COMPLIANCE]: team.legalAndCompliance ?? [],
});

const mapMembersByRole = (
    membersByRole: MembersByRole,
    updateMembers: (members: EditableMember[], role: AssignableRole) => EditableMember[],
): MembersByRole =>
    ASSIGNABLE_ROLES.reduce(
        (nextMembersByRole, role) => ({
            ...nextMembersByRole,
            [role]: updateMembers(membersByRole[role], role),
        }),
        buildEmptyMembersByRole(),
    );

const getInitials = (name: string) =>
    name
        .split(' ')
        .map((part) => part.trim())
        .filter(Boolean)
        .slice(0, 2)
        .map((part) => part[0]?.toUpperCase())
        .join('');
